import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPencilAlt, faTrashAlt, faSave } from '@fortawesome/free-solid-svg-icons';
import APICom from '../../utils/helpers/APICom';

const styles = {
  sectionBox: {
    border: "1px solid #000",
    padding: "1rem",
    marginBottom: "1.5rem",
  },
  header: {
    backgroundColor: "#87CEFA",
    color: "#fff",
    padding: "0.5rem",
    marginBottom: "0.5rem",
    width: "25%",
    marginLeft: "auto",
    marginRight: "auto",
    textAlign: "center",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    background: "#fff",
    padding: "8px 12px",
    textAlign: "left",
    border: "1px solid #000",
    fontWeight: 600,
    color: "#212529",
    fontSize: 13,
  },
  td: { padding: "8px 12px", border: "1px solid #dee2e6", color: "#212529", fontSize: 13 },
  input: { width: 90, border: "1px solid #ced4da", borderRadius: 4, padding: "3px 6px", fontSize: 13 },
  btnAdd:  { background: "#0d6efd", color: "#fff", border: "none", borderRadius: 4, width: 30, height: 30, cursor: "pointer", marginRight: 4, fontSize: 16, fontWeight: "bold", display: "inline-flex", alignItems: "center", justifyContent: "center" },
  btnEdit: { background: "#ffc107", color: "#fff", border: "none", borderRadius: 4, width: 30, height: 30, cursor: "pointer", marginRight: 4, fontSize: 13, display: "inline-flex", alignItems: "center", justifyContent: "center" },
  btnDel:  { background: "#dc3545", color: "#fff", border: "none", borderRadius: 4, width: 30, height: 30, cursor: "pointer", fontSize: 13, display: "inline-flex", alignItems: "center", justifyContent: "center" },
};

export default function RouteBreakGeneralRules({ sharedData, setConfirmationModalObj1 }) {
  const [rows, setRows]           = useState([]);
  const [nextId, setNextId]       = useState(1);
  const [editingId, setEditingId] = useState(null);
  const [editVals, setEditVals]   = useState({});
  const xCancelSource             = useRef(null);

  // ─── Fetch on mount / whenever settingID changes ───────────────────────
  useEffect(() => {
    if (sharedData != null) {
      fetchRules();
    } else {
      setRows([]);
    }
  }, [sharedData]);

  // ─── GET ───────────────────────────────────────────────────────────────
  const fetchRules = async () => {
    try {
      xCancelSource.current = APICom.GenerateSource();
      let response = await APICom.fetchData(
        "GenericManifestDotNetCoreURL",
        `api/Settings/GetMVRouteBreakGeneralRules?iSettingID=${sharedData}`,
        "get",
        {},
        {},
        xCancelSource.current
      );

      if (response?.data) {
        // Map API fields to local field names
        const mapped = response.data.map((item, index) => ({
          id:         item.iID        ?? index + 1,
          settingId:  item.iSettingID ?? sharedData,
          minShift:   parseFloat(item.dRouteMinimumShiftDuration ?? 0),
          maxShift:   parseFloat(item.dRouteMaximumShiftDuration ?? 0),
          breakDur:   parseFloat(item.dRouteBreakDuration        ?? 0),
          fromDB:     true,
        }));
        setRows(mapped);
        setNextId(mapped.length > 0 ? Math.max(...mapped.map(r => r.id)) + 1 : 1);
      } else {
        setRows([]);
      }
    } catch (error) {
      console.error("Error fetching Route Break General Rules:", error);
    }
  };

  // ─── ADD ───────────────────────────────────────────────────────────────
  function addRow() {
    if (sharedData == null) {
      setConfirmationModalObj1(prev => ({
        ...prev, show: true,
        title: "Validation",
        body: "Select a Setting first",
        btnOkText: "OK"
      }));
      return;
    }
    const newRow = { id: nextId, settingId: sharedData, minShift: "", maxShift: "", breakDur: "" };
    setRows([...rows, newRow]);
    setNextId(nextId + 1);
    setEditingId(newRow.id);
    setEditVals({ minShift: "", maxShift: "", breakDur: "" });
  }

  // Safely round any numeric/string input to exactly 2 decimal places.
  // Guards against floating point drift (e.g. 8.1 + 0.2 = 8.299999999999999)
  // and ensures the value sent to the DECIMAL(5,2) column is always clean.
  function toFixed2Number(val) {
    const num = parseFloat(val);
    if (isNaN(num)) return 0;
    return Math.round(num * 100) / 100;
  }

  // ─── SAVE (Add or Update) ──────────────────────────────────────────────
  const saveEdit = async (id) => {
    const minShift = toFixed2Number(editVals.minShift || 0);
    const maxShift = toFixed2Number(editVals.maxShift || 0);
    const breakDur = toFixed2Number(editVals.breakDur || 0);

    // ── Validation ──────────────────────────────────────────────────────
    if (editVals.minShift === "" || editVals.maxShift === "" || editVals.breakDur === "") {
      setConfirmationModalObj1(prev => ({
        ...prev, show: true,
        title: "Validation",
        body: "All fields (Min Shift, Max Shift, Break Duration) are required.",
        btnOkText: "OK"
      }));
      return;
    }
    if (minShift <= 0 || maxShift <= 0 || breakDur <= 0) {
      setConfirmationModalObj1(prev => ({
        ...prev, show: true,
        title: "Validation",
        body: "All values must be greater than zero.",
        btnOkText: "OK"
      }));
      return;
    }
    if (minShift > maxShift) {
      setConfirmationModalObj1(prev => ({
        ...prev, show: true,
        title: "Validation",
        body: "Min Shift Duration must be less than or equal to Max Shift Duration.",
        btnOkText: "OK"
      }));
      return;
    }
    // Break Duration is in MINUTES, Max Shift Duration is in HOURS —
    // convert break minutes to hours before comparing so the check is meaningful.
    const breakDurInHours = breakDur / 60;
    if (breakDurInHours > maxShift) {
      setConfirmationModalObj1(prev => ({
        ...prev, show: true,
        title: "Validation",
        body: "Break Duration must be less than or equal to Max Shift Duration.",
        btnOkText: "OK"
      }));
      return;
    }
    // DECIMAL(5,2): max value 999.99
    if (minShift > 999.99 || maxShift > 999.99 || breakDur > 999.99) {
      setConfirmationModalObj1(prev => ({
        ...prev, show: true,
        title: "Validation",
        body: "Values cannot exceed 999.99 minutes.",
        btnOkText: "OK"
      }));
      return;
    }
    // ────────────────────────────────────────────────────────────────────

    const parsed = {
      iID:                        id,
      iSettingID:                 sharedData,
      // Final safety net before hitting the API — guarantees clean 2-decimal
      // numeric values so SQL never throws "Error converting data type
      // numeric to decimal" due to floating point precision issues.
      dRouteMinimumShiftDuration: Number(minShift.toFixed(2)),
      dRouteMaximumShiftDuration: Number(maxShift.toFixed(2)),
      dRouteBreakDuration:        Number(breakDur.toFixed(2)),
    };

    // Determine if this is a new row (id not yet in DB — negative or nextId-style)
    const isNew = !rows.find(r => r.id === id)?.fromDB;

    try {
      xCancelSource.current = APICom.GenerateSource();

      if (isNew) {
        // ADD
        let response = await APICom.fetchData(
          "GenericManifestDotNetCoreURL",
          `api/Settings/AddMVRouteBreakGeneralRule`,
          "post",
          {},
          {
            iID:                        -1,
            iSettingID:                 parsed.iSettingID,
            dRouteMinimumShiftDuration: parsed.dRouteMinimumShiftDuration,
            dRouteMaximumShiftDuration: parsed.dRouteMaximumShiftDuration,
            dRouteBreakDuration:        parsed.dRouteBreakDuration,
          },
          xCancelSource.current
        );

        if (response?.data?.[0]?.iID) {
          setConfirmationModalObj1(prev => ({
            ...prev, show: true,
            title: "Route Break Rules",
            body: "Rule added successfully",
            btnOkText: "OK"
          }));
          fetchRules(); // Refresh to get real iID from DB
        } else {
          setConfirmationModalObj1(prev => ({
            ...prev, show: true,
            title: "Route Break Rules",
            body: "Failed to add Rule",
            btnOkText: "OK"
          }));
        }
      } else {
        // UPDATE
        let response = await APICom.fetchData(
          "GenericManifestDotNetCoreURL",
          `api/Settings/UpdateMVRouteBreakGeneralRule`,
          "post",
          {},
          parsed,
          xCancelSource.current
        );

        if (response?.data) {
          setConfirmationModalObj1(prev => ({
            ...prev, show: true,
            title: "Route Break Rules",
            body: "Rule updated successfully",
            btnOkText: "OK"
          }));
          fetchRules();
        } else {
          setConfirmationModalObj1(prev => ({
            ...prev, show: true,
            title: "Route Break Rules",
            body: "Failed to update Rule",
            btnOkText: "OK"
          }));
        }
      }
    } catch (error) {
      console.error("Error saving Route Break General Rule:", error);
    }

    setEditingId(null);
  };

  // ─── DELETE ────────────────────────────────────────────────────────────
  const deleteRow = async (id) => {
    const row = rows.find(r => r.id === id);

    // If row was never saved to DB just remove from local state
    if (!row?.fromDB) {
      setRows(rows.filter(r => r.id !== id));
      if (editingId === id) setEditingId(null);
      return;
    }

    try {
      xCancelSource.current = APICom.GenerateSource();
      let response = await APICom.fetchData(
        "GenericManifestDotNetCoreURL",
        `api/Settings/DeleteMVRouteBreakGeneralRule`,
        "post",
        {},
        { iID: id },
        xCancelSource.current
      );

      if (response?.data === true || response?.data) {
        setConfirmationModalObj1(prev => ({
          ...prev, show: true,
          title: "Route Break Rules",
          body: "Rule deleted successfully",
          btnOkText: "OK"
        }));
        fetchRules();
      } else {
        setConfirmationModalObj1(prev => ({
          ...prev, show: true,
          title: "Route Break Rules",
          body: "Failed to delete Rule",
          btnOkText: "OK"
        }));
      }
    } catch (error) {
      console.error("Error deleting Route Break General Rule:", error);
    }
  };

  // ─── Local edit handlers ───────────────────────────────────────────────
  function startEdit(row) {
    setEditingId(row.id);
    setEditVals({
      minShift: row.minShift,
      maxShift: row.maxShift,
      breakDur: row.breakDur,
    });
  }

  function toDecimal999(val) {
    if (val === "") return val;
    const cleaned = val.replace(/[^0-9.]/g, "").replace(/(\..*)\./, "$1");
    const [intPart, decPart] = cleaned.split(".");
    // Max 3 integer digits — DECIMAL(5,2) column limit (max 999.99)
    let clampedInt = intPart.slice(0, 3);
    if (parseInt(clampedInt) > 999) clampedInt = "999";
    if (decPart !== undefined) {
      // If integer is 999, decimal must be 99 (DECIMAL(5,2) ceiling)
      const clampedDec = parseInt(clampedInt) === 999 ? decPart.slice(0, 2) : decPart.slice(0, 2);
      return clampedInt + "." + clampedDec;
    }
    return clampedInt;
  }

  function handleChange(field, val) {
    const decimalFields = ["minShift", "maxShift", "breakDur"];
    const processed = decimalFields.includes(field) ? toDecimal999(val) : val.replace(/[^0-9]/g, "");
    setEditVals({ ...editVals, [field]: processed });
  }

  function formatDecimal(val) {
    if (val === "" || val === null || val === undefined) return "";
    const num = parseFloat(val);
    return isNaN(num) ? "" : num.toFixed(2);
  }

  function onKeyDownNumeric(e) {
    const allowed = ["Backspace","Delete","Tab","ArrowLeft","ArrowRight","ArrowUp","ArrowDown"];
    if (allowed.includes(e.key)) return;

    // Only allow digits and one decimal point
    if (!/^\d$/.test(e.key) && e.key !== ".") {
      e.preventDefault();
      return;
    }

    const val = e.target.value;
    const cursorPos = e.target.selectionStart;
    const selectionEnd = e.target.selectionEnd;
    const selected = selectionEnd - cursorPos; // characters selected (will be replaced)
    const dotIndex = val.indexOf(".");

    // Block second decimal point
    if (e.key === "." && val.includes(".")) {
      e.preventDefault();
      return;
    }

    // Simulate what value will look like after this keypress
    const newVal = val.slice(0, cursorPos) + e.key + val.slice(selectionEnd);
    const newDotIndex = newVal.indexOf(".");

    if (newDotIndex === -1) {
      // No decimal — integer part only, max 3 digits (DECIMAL(5,2) limit)
      if (newVal.length > 3) {
        e.preventDefault();
        return;
      }
      if (parseFloat(newVal) > 999) {
        e.preventDefault();
      }
    } else {
      const newIntPart = newVal.slice(0, newDotIndex);
      const newDecPart = newVal.slice(newDotIndex + 1);
      // Max 3 integer digits, max 2 decimal digits
      if (newIntPart.length > 3 || newDecPart.length > 2) {
        e.preventDefault();
        return;
      }
      // Max value 999.99 — DECIMAL(5,2) column limit
      if (parseFloat(newVal) > 999.99) {
        e.preventDefault();
      }
    }
  }
  const isEditing = (id) => editingId === id;

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <div style={styles.sectionBox}>
      <div style={styles.header}>Route Break General Rules</div>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Actions</th>
            <th style={styles.th}>Min Shift Duration (hrs)</th>
            <th style={styles.th}>Max Shift Duration (hrs)</th>
            <th style={styles.th}>Break Duration (mins)</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td style={styles.td} colSpan={4} align="center">
                <button style={styles.btnAdd} onClick={addRow} title="Add">
                  <FontAwesomeIcon icon={faPlus} />
                </button>
              </td>
            </tr>
          )}
          {rows.map((row) => (
            <tr key={row.id}>
              <td style={styles.td}>
                <button style={styles.btnAdd} onClick={addRow} title="Add">
                  <FontAwesomeIcon icon={faPlus} />
                </button>
                <button
                  style={styles.btnEdit}
                  onClick={() => isEditing(row.id) ? saveEdit(row.id) : startEdit(row)}
                  title={isEditing(row.id) ? "Save" : "Edit"}
                >
                  {isEditing(row.id)
                    ? <FontAwesomeIcon icon={faSave} />
                    : <FontAwesomeIcon icon={faPencilAlt} />}
                </button>
                <button style={styles.btnDel} onClick={() => deleteRow(row.id)} title="Delete">
                  <FontAwesomeIcon icon={faTrashAlt} />
                </button>
              </td>

              <td style={styles.td}>
                {isEditing(row.id)
                  ? <input style={styles.input} type="text" inputMode="decimal" maxLength={6} placeholder="0.00" value={editVals.minShift} onKeyDown={onKeyDownNumeric}
                      onChange={(e) => handleChange("minShift", e.target.value)} />
                  : formatDecimal(row.minShift)}
              </td>
              <td style={styles.td}>
                {isEditing(row.id)
                  ? <input style={styles.input} type="text" inputMode="decimal" maxLength={6} placeholder="0.00" value={editVals.maxShift} onKeyDown={onKeyDownNumeric}
                      onChange={(e) => handleChange("maxShift", e.target.value)} />
                  : formatDecimal(row.maxShift)}
              </td>
              <td style={styles.td}>
                {isEditing(row.id)
                  ? <input style={styles.input} type="text" inputMode="decimal" maxLength={6} placeholder="0.00" value={editVals.breakDur} onKeyDown={onKeyDownNumeric}
                      onChange={(e) => handleChange("breakDur", e.target.value)} />
                  : formatDecimal(row.breakDur)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
